import { type CreateCompanyFormData, createCompanySchema } from '@/components/companies/schema';
import { TeamMemberCell } from '@/components/companies/team-member-cell';
import { Icons } from '@/components/icon';
// import { Phone, PhonePopover } from '@/components/table/cells/phone';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Label } from '@/components/ui/label';
import {
  Modal,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { H4, P3 } from '@/components/ui/text';
import { useTranslation } from '@/hooks/use-translation';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import type { components } from '@/services/api-allo/api-allo-v1';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  useAddContactsToCompanyMutation,
  useCreateCompanyMutation,
} from '../queries/use-company-mutations';

type CompanyResponse = components['schemas']['CompanyResponse'];

interface CreateCompanyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (company: CompanyResponse) => void;
  initialName?: string;
  initialTeamMemberIds?: string[];
  initialTeamMembers?: Array<{ id: string; name: string; last_name: string }>;
}

export function CreateCompanyModal({
  open,
  onOpenChange,
  onSuccess,
  initialName,
  initialTeamMemberIds,
  initialTeamMembers,
}: CreateCompanyModalProps) {
  const { t } = useTranslation('desktop_ui');

  // const [phonePopoverOpen, setPhonePopoverOpen] = useState(false);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const contentTopRef = useRef<HTMLDivElement>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<CreateCompanyFormData>({
    resolver: zodResolver(createCompanySchema, {
      errorMap: (issue) => {
        const messageMap: Record<string, string> = {
          name_required: t('components.companies.validation.nameRequired'),
          invalid_url: t('components.companies.validation.invalidUrl'),
        };
        const msg = issue.message ?? '';
        return { message: messageMap[msg] ?? msg };
      },
    }),
    defaultValues: {
      name: '',
      teamMemberIds: [],
      phoneNumbers: [],
      // website: '',
    },
    mode: 'onSubmit',
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      // Track modal open
      analytics.track(ANALYTICS_EVENTS.COMPANIES.CREATION_STARTED);
      // Set initial values when opening
      reset({
        name: initialName ?? '',
        teamMemberIds: initialTeamMemberIds ?? [],
        phoneNumbers: [],
      });
    } else {
      reset();
      // setPhonePopoverOpen(false);
      setShowDiscardConfirmation(false);
    }
  }, [open, reset, initialName, initialTeamMemberIds]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardConfirmation(true);
    } else {
      onOpenChange(false);
    }
  }, [isDirty, onOpenChange]);

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardConfirmation(false);
    onOpenChange(false);
  }, [onOpenChange]);

  // Track pending team member IDs to add after company creation
  const pendingTeamMemberIdsRef = useRef<string[]>([]);

  const addContactsMutation = useAddContactsToCompanyMutation({
    showToast: false,
  });

  const createCompanyMutation = useCreateCompanyMutation({
    onSuccess: (company) => {
      // Track company created
      analytics.track(ANALYTICS_EVENTS.COMPANIES.CREATED, {
        company_id: company.id,
        company_name: company.name,
        team_members_count: pendingTeamMemberIdsRef.current.length,
      });
      // Add team members to the company if any were selected
      if (company.id && pendingTeamMemberIdsRef.current.length > 0) {
        addContactsMutation.addContacts(company.id, pendingTeamMemberIdsRef.current);
      }
      pendingTeamMemberIdsRef.current = [];
      onSuccess?.(company);
      onOpenChange(false);
    },
  });

  const onSubmit = useCallback(
    (data: CreateCompanyFormData) => {
      // Store team member IDs to add after company creation
      pendingTeamMemberIdsRef.current = data.teamMemberIds ?? [];

      createCompanyMutation.mutate({
        body: {
          name: data.name.trim(),
        },
      });
    },
    [createCompanyMutation],
  );

  const scrollToTop = useCallback(() => {
    const viewport = contentTopRef.current
      ?.closest('[data-slot="modal-content"]')
      ?.querySelector('[data-radix-scroll-area-viewport]');
    viewport?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleConfirm = useCallback(() => {
    handleSubmit(onSubmit, scrollToTop)();
  }, [handleSubmit, onSubmit, scrollToTop]);

  const handleModalOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && isDirty) {
        setShowDiscardConfirmation(true);
      } else {
        onOpenChange(newOpen);
      }
    },
    [isDirty, onOpenChange],
  );

  return (
    <Modal open={open} onOpenChange={handleModalOpenChange}>
      <ModalContainer>
        <ModalHeader
          icon={Icons.BuildingOffice}
          title={t('components.companies.form.createTitle')}
          onClose={handleClose}
        />
        <ModalContent>
          <div ref={contentTopRef} />
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <FormField
                className="mb-3"
                label={t('components.companies.form.name')}
                required
                error={!!errors.name}
              >
                <input
                  type="text"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('components.companies.form.namePlaceholder')}
                  className="w-full bg-transparent font-inter text-s font-medium text-primary-100 outline-none placeholder:text-primary-40"
                />
              </FormField>
            )}
          />

          <Controller
            control={control}
            name="teamMemberIds"
            render={({ field }) => (
              <div className="mb-3 flex w-full flex-col gap-1">
                <Label className="px-0.5 text-s text-primary-60 gap-0.5">
                  {t('components.companies.form.team')}
                </Label>
                <TeamMemberCell
                  members={initialTeamMembers}
                  selectedMemberIds={field.value ?? []}
                  onMembersChange={(ids) => field.onChange(ids)}
                  renderTrigger={(displayContent) => (
                    <button
                      type="button"
                      className="flex h-7 w-full cursor-pointer items-center rounded-md border border-primary-10 bg-white px-2 py-1 hover:bg-primary-3 focus-within:border-highlight"
                    >
                      {displayContent}
                    </button>
                  )}
                />
              </div>
            )}
          />

          {/*<Controller
            control={control}
            name="phoneNumbers"
            render={({ field }) => (
              <PhonePopover
                phoneNumbers={field.value ?? []}
                open={phonePopoverOpen}
                onOpenChange={setPhonePopoverOpen}
                onAddPhone={(phone) => {
                  field.onChange([...(field.value ?? []), phone]);
                }}
                onDeletePhone={(phone) => {
                  field.onChange((field.value ?? []).filter((p) => p !== phone));
                }}
                sideOffset={-30}
              >
                <FormField className="mb-3" label={t('components.companies.form.phoneNumbers')}>
                  {!field.value || field.value.length === 0 ? (
                    <button
                      type="button"
                      onClick={() => setPhonePopoverOpen(true)}
                      className="w-full cursor-pointer text-left font-inter text-s font-medium text-primary-40 outline-none"
                    >
                      {t('components.companies.form.phoneNumbersPlaceholder')}
                    </button>
                  ) : (
                    <div className="group flex w-full items-center gap-1">
                      <div className="flex flex-1 flex-wrap items-center gap-1 overflow-hidden">
                        {field.value.map((phone) => (
                          <Phone key={phone} phoneNumber={phone} tabIndex={-1} />
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setPhonePopoverOpen(true)}
                        className="shrink-0 cursor-pointer font-inter text-s font-medium text-primary-40 opacity-0 transition-opacity group-hover:opacity-100 hover:text-primary-60"
                      >
                        {t('components.companies.form.edit')}
                      </button>
                    </div>
                  )}
                </FormField>
              </PhonePopover>
            )}
          />*/}

          {/* <Controller
            control={control}
            name="website"
            render={({ field }) => (
              <FormField
                className="mb-3"
                label={t('components.companies.form.website')}
                error={!!errors.website}
              >
                <input
                  type="text"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder={t('components.companies.form.websitePlaceholder')}
                  className="w-full bg-transparent font-inter text-s font-medium text-primary-100 outline-none placeholder:text-primary-40"
                />
              </FormField>
            )}
          /> */}
        </ModalContent>
        <ModalFooter
          primaryLabel={t('components.companies.form.create')}
          onConfirm={handleConfirm}
          disabled={createCompanyMutation.isPending}
        />
      </ModalContainer>

      <Dialog open={showDiscardConfirmation} onOpenChange={setShowDiscardConfirmation}>
        <DialogContent
          className="w-[400px] overflow-hidden rounded-2xl p-0 gap-0"
          showCloseIcon={false}
        >
          <DialogHeader className="gap-1 px-4 py-3">
            <DialogTitle>
              <H4 className="text-primary-100">
                {t('components.companies.form.discardConfirmation.title')}
              </H4>
            </DialogTitle>
            <DialogDescription>
              <P3 className="text-primary-60">
                {t('components.companies.form.discardConfirmation.description')}
              </P3>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 border-t border-primary-5 p-3">
            <button
              type="button"
              onClick={() => setShowDiscardConfirmation(false)}
              className="cursor-pointer flex h-6 items-center justify-center rounded-md border border-primary-10 bg-white px-2 hover:bg-primary-5 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-40"
            >
              <P3 className="text-primary-100">
                {t('components.companies.form.discardConfirmation.cancel')}
              </P3>
            </button>
            <button
              type="button"
              onClick={handleConfirmDiscard}
              className="cursor-pointer h-6 flex items-center justify-center rounded-md border border-status-danger bg-extra-decline px-2 hover:bg-extra-decline"
            >
              <P3 className="text-white">
                {t('components.companies.form.discardConfirmation.confirm')}
              </P3>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </Modal>
  );
}
